import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { CopyField } from '../../components/CopyField.js';
import { PUBLIC_ORIGIN } from '../../joinLinks.js';
import type { RadioSettings } from '../../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;

/**
 * Radio settings (decision 2026-09-29): the Icecast stream (https) that players with a music
 * permission hear. Permissions are given per player in Users.
 */
export function RadioAdmin() {
  const [radio, setRadio] = useState<RadioSettings | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ radio: RadioSettings }>('GET', '/api/admin/radio')
      .then((r) => setRadio(r.radio))
      .catch((err) => setFlash({ ok: false, text: t.errors[err instanceof ApiError ? err.code : 'network'] }));
  }, []);

  const save = async (e: Event) => {
    e.preventDefault();
    if (!radio) return;
    const url = radio.url.trim();
    if (url && !url.startsWith('https://')) return setFlash({ ok: false, text: t.admin.radio.httpsOnly });
    setBusy(true);
    try {
      const r = await api<{ radio: RadioSettings }>('PUT', '/api/admin/radio', { ...radio, url });
      setRadio(r.radio);
      setFlash({ ok: true, text: t.admin.radio.saved });
    } catch (err) {
      setFlash({ ok: false, text: t.errors[err instanceof ApiError ? err.code : 'network'] });
    } finally {
      setBusy(false);
    }
  };

  const r = t.admin.radio;
  return (
    <form class="card form" onSubmit={save}>
      <h2>{r.title}</h2>
      <p class="hint">{r.info}</p>
      {radio && (
        <>
          <label class="switch">
            <input type="checkbox" checked={radio.enabled} onChange={(e) => setRadio({ ...radio, enabled: e.currentTarget.checked })} />
            <span class="switch-track" aria-hidden="true" />
            {r.enabled}
          </label>
          <label>
            {r.name}
            <input value={radio.name} maxLength={40} onInput={(e) => setRadio({ ...radio, name: e.currentTarget.value })} required />
          </label>
          <label>
            {r.url}
            <input
              type="url"
              value={radio.url}
              placeholder="https://…"
              maxLength={300}
              onInput={(e) => setRadio({ ...radio, url: e.currentTarget.value })}
            />
            <small>{r.urlHint}</small>
          </label>
          <p class="hint">{r.permissions}</p>

          <h3>{r.publicTitle}</h3>
          <label class="switch">
            <input
              type="checkbox"
              checked={radio.publicPlayer}
              onChange={(e) => setRadio({ ...radio, publicPlayer: e.currentTarget.checked })}
            />
            <span class="switch-track" aria-hidden="true" />
            {r.publicPlayer}
          </label>
          <p class="hint">{r.publicInfo}</p>
          {radio.publicPlayer && <CopyField value={`${PUBLIC_ORIGIN}/kuuntele/`} label={r.publicLink} />}
          {/* Guest links with the public player are reserved (radio.publicGuestOffer), not shown yet. */}

          <h3>{r.guestTitle}</h3>
          <label class="switch">
            <input type="checkbox" checked={radio.guestRadio} onChange={(e) => setRadio({ ...radio, guestRadio: e.currentTarget.checked })} />
            <span class="switch-track" aria-hidden="true" />
            {r.guestRadio}
          </label>
          <p class="hint">{r.guestRadioInfo}</p>
        </>
      )}
      {flash && (
        <p class={flash.ok ? 'success' : 'error'} role="status">
          {flash.text}
        </p>
      )}
      <button class="btn btn-primary" type="submit" disabled={busy || !radio}>
        {t.admin.save}
      </button>
    </form>
  );
}
